import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Mutation, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { emailRegexCheck } from '@adornis/users/db/validators.js';
import { generateLoginToken, verifyToken } from '@adornis/users/server/token.js';
import { getUserFromContext } from '@adornis/users/server/util.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { validateIban } from '@adornis/validation/functions/iban.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import type { Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import { from, mapTo, type Observable, switchMap } from 'rxjs';
import { ChancenwerkCounter } from '../../../counters.js';
import { MailEventLog, MailEventLogType } from '../../../eventlogs/MailEventlog.js';
import { onboardingConfirmation, registerEmployee, registerEmployeeOnboarding } from '../../../mailer/mailTemplates.js';
import { chancenWerkMailer } from '../../../mailer/server/mailer.js';
import { ChancenPermission } from '../../../permissions.js';
import { ChancenRole } from '../../../semantics/ChancenRole.js';
import { Role } from '../../../semantics/Role.js';
import { SemanticRelationship } from '../../../semantics/SemanticRelationship.js';
import { Taetigkeit } from '../../../semantics/Taetigkeit.js';
import { getMitarbeiterFromSoftgardenKeyFromCache } from '../../../softgarden/softgardenImport.js';
import { createAllOnboardingPdfAttachments } from '../../onboardingPdfGenerator.js';
import { ChancenwerkUser } from './ChancenwerkUser.js';
import { CRVertrag } from './CRVertrag.js';

export enum Vertragsarten {
  ANSTELLUNG = 'Anstellung',
  EHRENAMTLER = 'Ehrenamtler (bis 840 Euro)',
  MINIJOB = 'Minijob',
  PRAKTIKUM = 'Praktikum',
  UEBUNGSLEITER = 'Übungsleiter (bis 3.000 Euro)',
  WERKSTUDENT = 'Werkstudent',
  VERWALTUNGSMITARBEITER = 'VerwaltungsMitarbeiter',
}

export const MITARBEITER_KUENDIGUNGSGRUENDE = [
  'Prüfungsphase / Lernstress',
  'Keine Vereinbarkeit mit Stundenplan',
  'Zeitmangel durch Uni / Projekte / Hausarbeiten',
  'Auslandssemester / Erasmus / Uniwechsel',
  'Praktikum oder Werkstudentenstelle gefunden',
  'Studium abgeschlossen',
  'Mehr Freizeit oder Work-Life-Balance gewünscht',
  'Unzufriedenheit mit Bezahlung',
  'Schlechte Arbeitszeiten / zu wenig Flexibilität',
  'Mangelnde Wertschätzung / schlechtes Organisationsklima',
  'Körperliche oder psychische Überlastung',
  'Job entspricht nicht den eigenen Interessen',
  'Lange Anfahrtswege',
  'Wunsch nach beruflicher Veränderung / Orientierung',
  'Probleme mit Kolleg:innen/ vorgesetzen Personen',
  'Konflikt mit der Schule',
  'anderes Jobangebot',
  'Umzug',
  'angenommene Vollzeitstelle',
  'Andere Verpflichtungen angenommen (im privaten Bereich)',
  'Zu wenige Wochenstunden',
];

/**
 * @entity WorkAvailablility
 */
@Entity()
export class WorkAvailablility extends AdornisEntity {
  public static override _class = 'WorkAvailablility';

  public static override resolveID(entity: any): string {
    return entity.weekday;
  }

  @Field(() => String)
  public weekday: Maybe<typeof Weekday>;

  @Field(() => DateTime)
  public from: Maybe<DateTime>;

  @Field(() => DateTime)
  public to: Maybe<DateTime>;
}

/**
 * @entity WorkshopsAndTrainingInformation
 */
@Entity()
export class WorkshopsAndTrainingInformation extends AdornisEntity {
  public static override _class = 'WorkshopsAndTrainingInformation';

  // OS Workshops
  @Field(() => DateTime)
  public participatedOSWorkshopOneRoleModel: Maybe<DateTime>;

  @Field(() => DateTime)
  public carriedOutOSWorkshopOneRoleModel: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedOSWorkshopTwoChallenges: Maybe<DateTime>;

  @Field(() => DateTime)
  public carriedOutOSWorkshopTwoChallenges: Maybe<DateTime>;

  // SuS Workshops
  @Field(() => DateTime)
  public participatedSuSWorkshopLearningToLearn: Maybe<DateTime>;

  @Field(() => DateTime)
  public carriedOutSuSWorkshopLearningToLearn: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedSuSWorkshopMentalStrength: Maybe<DateTime>;

  @Field(() => DateTime)
  public carriedOutSuSWorkshopMentalStrength: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedSusWorkshopInnerStrenght: Maybe<DateTime>;

  @Field(() => DateTime)
  public carriedOutSusWorkshopInnerStrenght: Maybe<DateTime>;

  // ÜL Workshops
  @Field(() => DateTime)
  public participatedULTrainingOSCompanion: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopCompentenceOne: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopCompentenceTwo: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopMotivation: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopToDos: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopStress: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopCommunication: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopStorytelling: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopLanguage: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULWorkshopDidacticReduction: Maybe<DateTime>;

  @Field(() => DateTime)
  public participatedULHeftCampusKurve: Maybe<DateTime>;

  public static workshopTypes = {
    participatedOSWorkshopOneRoleModel: 'Vorbild sein',
    participatedOSWorkshopTwoChallenges: 'Umgang mit Herausforderungen',
    participatedSuSWorkshopLearningToLearn: 'Lernen lernen',
    participatedSuSWorkshopMentalStrength: 'Mentale Stärke',
    participatedSusWorkshopInnerStrenght: 'Deine innere Kraft',
    participatedULTrainingOSCompanion: 'Einführung OS-Begleitung & Akquise',
    participatedULWorkshopCompentenceOne: 'Interkulturelle Kompetenzen - Teil 1',
    participatedULWorkshopCompentenceTwo: 'Interkulturelle Kompetenzen - Teil 2',
    participatedULWorkshopMotivation: 'Motivationsworkshop...gar kein Bock!',
    participatedULWorkshopToDos: "Ich & meine ToDo's",
    participatedULWorkshopStress: 'Wenn der Stress dich stresst',
    participatedULWorkshopCommunication: 'Kann man nicht nicht kommunizieren?',
    participatedULWorkshopStorytelling: 'Identifikation & Motivation durch Storytelling',
    participatedULWorkshopLanguage: 'Sprache & Sprachsensibilität im Fachunterricht',
    participatedULWorkshopDidacticReduction: 'Diaktische Reduktion',
    participatedULHeftCampusKurve: 'CHANCENhefte, CHANCENcampus & CHANCENkurve',
  };

  public static workshopMigration: Record<string, { training: string; carriedOut: Maybe<string> }> = {
    'Vorbild sein': { training: 'participatedOSWorkshopOneRoleModel', carriedOut: 'carriedOutOSWorkshopOneRoleModel' },
    'Umgang mit Herausforderungen': {
      training: 'participatedOSWorkshopTwoChallenges',
      carriedOut: 'carriedOutOSWorkshopTwoChallenges',
    },
    'Lernen lernen': {
      training: 'participatedSuSWorkshopLearningToLearn',
      carriedOut: 'carriedOutSuSWorkshopLearningToLearn',
    },
    'Mentale Stärke': {
      training: 'participatedSuSWorkshopMentalStrength',
      carriedOut: 'carriedOutSuSWorkshopMentalStrength',
    },
    'Deine innere Kraft': {
      training: 'participatedSusWorkshopInnerStrenght',
      carriedOut: 'carriedOutSusWorkshopInnerStrenght',
    },
    'Einführung OS-Begleitung & Akquise': { training: 'participatedULTrainingOSCompanion', carriedOut: null },
    'Interkulturelle Kompetenzen - Teil 1': { training: 'participatedULWorkshopCompentenceOne', carriedOut: null },
    'Interkulturelle Kompetenzen - Teil 2': { training: 'participatedULWorkshopCompentenceTwo', carriedOut: null },
    'Motivationsworkshop...gar kein Bock!': { training: 'participatedULWorkshopMotivation', carriedOut: null },
    "Ich & meine ToDo's": { training: 'participatedULWorkshopToDos', carriedOut: null },
    'Wenn der Stress dich stresst': { training: 'participatedULWorkshopStress', carriedOut: null },
    'Kann man nicht nicht kommunizieren?': { training: 'participatedULWorkshopCommunication', carriedOut: null },
    'Identifikation & Motivation durch Storytelling': {
      training: 'participatedULWorkshopStorytelling',
      carriedOut: null,
    },
    'Sprache & Sprachsensibilität im Fachunterricht': { training: 'participatedULWorkshopLanguage', carriedOut: null },
    'Diaktische Reduktion': { training: 'participatedULWorkshopDidacticReduction', carriedOut: null },
    'CHANCENhefte, CHANCENcampus & CHANCENkurve': { training: 'participatedULHeftCampusKurve', carriedOut: null },
  };
}

/**
 * @entity KontoInformationen
 */
@Entity()
export class KontoInformationen extends AdornisEntity {
  public static override _class = 'KontoInformationen';

  @Field(() => String)
  public kontoinhaber!: string;

  // * We can't use iban() validator right now, because that would stop a lot of entities without a IBAN from being saveable.
  // * At the moment we only want to validate the IBAN if it is set.
  @validate(({ value, key, source }) => {
    if (!value || value === '') {
      return;
    }
    if (!validateIban(value)) {
      throw new ValidationError(`Bitte gebe eine korrekte IBAN ein!`, {
        key: 'iban',
        source,
        translationKey: 'ibanEmail',
      });
    }
  })
  @Field(() => String)
  public iban!: string;

  @Field(() => String)
  public bic!: string;

  @Field(() => String)
  public kreditinstitut!: string;
}

/**
 * @entity Unterlagen
 */
@Entity()
export class Unterlagen extends AdornisEntity {
  public static override _class = 'Unterlagen';

  @Field(() => DateTime)
  public vertragVersendet!: DateTime;

  @Field(() => DateTime)
  public vertragErhalten!: DateTime;

  @Field(() => DateTime)
  public zusatzvereinbarungVersendet!: DateTime;

  @Field(() => DateTime)
  public zusatzvereinbarungErhalten: Maybe<DateTime>;

  @Field(() => Boolean)
  public selbstverplichtungserklaerungErhalten!: boolean;

  @Field(() => Boolean)
  public datenschutzerklaerungErhalten!: boolean;

  // This is used as "Datum Führungszeugnis" since CW_02_26
  @Field(() => DateTime)
  public fuehrungzeugnisGueltigAb!: DateTime;

  @Field(() => Boolean)
  public fuehrungzeugnisEinsichtnahmeErfolgt: Maybe<boolean>;

  @Field(() => Boolean)
  public fuehrungzeugnisRelevanteEintraege: Maybe<boolean>;

  @Field(() => Boolean)
  public vereinbarungUrheberrechtErhalten!: boolean;

  @Field(() => Boolean)
  public byodFormularErhalten!: boolean;

  @Field(() => DateTime)
  public ersthelferkursAbsolviertAm?: Maybe<DateTime>;

  @Field(() => Boolean)
  public aktuelleMasernimpfungNachgewiesen!: boolean;

  @Field(() => Boolean)
  public einwilligungserklaerungMasernimpfungNachweisErhalten!: boolean;

  // Übungsleiterfreibetrag (§ 3 Nr. 26 EStG) — persönliche Erklärung des/der Mitarbeiter:in,
  // erfasst während des Onboardings und über die Laufzeit des Vertrags hinweg gültig.
  /** true = Freibetrag bereits / anderweitig in Anspruch genommen; false = nicht; null/undefined = noch nicht angegeben. */
  @Field(() => Boolean)
  public freibetragInAnspruchGenommen: Maybe<boolean>;

  /** In Anspruch genommener Betrag in € pro Kalenderjahr. */
  @Field(() => Int)
  public freibetragBetrag: Maybe<number>;

  @Field(() => String)
  public personalNummer!: string;

  @Field(() => String)
  public kreditorenNummer!: string;

  @Field(() => String, { default: v => v ?? '' })
  public kommunikationMitUL!: string;

  @Field(() => String, { default: v => v ?? '' })
  public sonstigeVermerke!: string;
}

/**
 * @entity Onboarding
 */
@Entity()
export class Onboarding extends AdornisEntity {
  public static override _class = 'Onboarding';

  constructor(doc: PartialEntityData<Onboarding>) {
    super(doc);
  }

  @Field(() => DateTime)
  public einarbeitungAbsolviertAm: Maybe<DateTime>;

  @Field(() => DateTime)
  public onboardingAkademieAbsolviertAm: Maybe<DateTime>;

  @Field(() => DateTime)
  public feedbackquizCompletedAt: Maybe<DateTime>;

  @Field(() => DateTime)
  public feedbacktalkCompletedAt: Maybe<DateTime>;

  @Field(() => DateTime)
  public trainingFinishedAt: Maybe<DateTime>;

  @Field(() => DateTime)
  public schoolSpecificTrainingFinishedAt: Maybe<DateTime>;

  /** Used to mark whether a new Mitarbeiter is fully onboarded. */
  @Field(() => Boolean, { default: v => v ?? false })
  public contractDocumentsConfirmed!: boolean;

  public static onboardinTypes = {
    einarbeitungAbsolviertAm: 'CHANCENspirit (CHANCENakademie)',
    onboardingAkademieAbsolviertAm: 'Einführung der Lernkaskade (CHANCENakademie)',
    feedbackquizCompletedAt: 'Feedback-Quiz',
    feedbacktalkCompletedAt: 'Feedbackgespräch',
    trainingFinishedAt: 'Einarbeitung abgeschlossen',
    schoolSpecificTrainingFinishedAt: 'Schulspezifische Einarbeitung',
  };
}

export enum MitarbeiterState {
  GUEST = 'guest',
  COMPLETE = 'complete',
}

/**
 * @entity Mitarbeiter
 */
@Entity()
export class Mitarbeiter extends ChancenwerkUser {
  public static override _class = 'Mitarbeiter';

  public static override readableName = 'Mitarbeiter:in';

  public static override get allFields() {
    return selectionSet(() => this, 8);
  }

  public get state(): MitarbeiterState {
    if (this.guestAccount) return MitarbeiterState.GUEST;
    return MitarbeiterState.COMPLETE;
  }

  constructor(doc: PartialEntityData<Mitarbeiter>) {
    super(doc);
  }

  @Field(() => Boolean, { default: v => v ?? false })
  public guestAccount: Maybe<boolean>;

  @Field(() => String)
  public override vorname?: string;

  @Field(() => String)
  public override nachname?: string;

  @Field(() => String)
  public employeeID?: Maybe<string>;

  @Field(() => String)
  public staatsangehoerigkeit?: string;

  @validate(({ value, key, source }) => {
    if (value && !emailRegexCheck(value as string))
      throw new ValidationError(`${value as string} ist keine gültige E-Mail.`, { key, source });
  })
  @Field(() => String)
  public emailChancenwerk?: string;

  @Field(() => String)
  public festnetz?: string;

  @Field(() => String)
  public handynummerChancenwerk?: string;

  @Field(() => String)
  public hochschule?: string;

  @Field(() => String)
  public studiengang?: string;

  @Field(() => Int)
  public grade?: number;

  @Field(() => String)
  public gradeExtraInfo?: string;

  @Field(() => String)
  public kuendigungsgrund?: string;

  @Field(() => Boolean, { default: v => v ?? false })
  public kontaktaufnahmeFestnetznummer?: boolean;

  @Field(() => [CRVertrag], { default: v => v ?? [] })
  public vertraege!: CRVertrag[];

  @Field(() => Unterlagen, { default: v => v ?? new Unterlagen({}) })
  public unterlagen!: Unterlagen;

  @Field(() => KontoInformationen, { default: v => v ?? new KontoInformationen({}) })
  public kontoInformationen!: KontoInformationen;

  @Field(() => Onboarding, { default: v => v ?? new Onboarding({}) })
  public onboarding!: Onboarding;

  @Field(() => WorkshopsAndTrainingInformation, { default: v => v ?? new WorkshopsAndTrainingInformation({}) })
  public workshopsAndTrainingInformation: Maybe<WorkshopsAndTrainingInformation>;

  @Field(() => [WorkAvailablility], { default: v => v ?? [] })
  public workAvailabilities: Maybe<WorkAvailablility[]>;

  public ensureWorkAvailabilities() {
    if (!this.workAvailabilities) this.workAvailabilities = [];
    return this.workAvailabilities;
  }

  public getWorkAvailability(weekday: Weekday) {
    // @ts-expect-error no idea
    const found = this.ensureWorkAvailabilities().find(w => w.weekday === weekday);
    if (!found) {
      const newWorkAvailability = new WorkAvailablility({ weekday });
      this.workAvailabilities?.push(newWorkAvailability);
      return newWorkAvailability;
    }
    return found;
  }

  static override async beforeCreate(entity: Mitarbeiter) {
    if (!entity.employeeID) entity.employeeID = (await ChancenwerkCounter.getIncrementingEmployeeID()()).toString();

    return super.beforeCreate(entity);
  }

  protected override async saveMailEventLog(error?: Error) {
    await new MailEventLog({
      userID: this._id,
      mailType: MailEventLogType.MITARBEITER_REGISTER,
      successful: !error,
      sendMailError: error?.message,
      emailAddress: this.email,
    }).create();
  }

  protected override async getComposedRegisterMail() {
    return Message.compose({
      subject: 'Willkommen auf dem CHANCENcampus',
      html: registerEmployee({
        username: this.employeeID ?? '',
        confirmationUrl: (await Mitarbeiter.getPasswordChangeLink(this._id, 'chancennetz')).toString(),
      }),
    });
  }

  private static async getOnboardingMagicLink(userID: string) {
    const token = await generateLoginToken({
      userID,
      userClass: ChancenwerkUser._class,
      forgetSession: true,
      ttu: -1,
      ttl: 1e3 * 60 * 60 * 24 * 14, // 14 days
    });
    // NETZ_URL by convention ends with a trailing slash (see chancennetz/.env.testing); concatenate the
    // path without a leading slash to avoid producing '.../api/...' becoming '...//api/...' in the link.
    const url = new URL(projectConfig.get('NETZ_URL') + 'api/onboarding-auth');
    url.searchParams.append('token', token);
    return url.toString();
  }

  protected async getComposedOnboardingMail() {
    return Message.compose({
      subject: 'Willkommen bei Chancenwerk – Deine Vereinbarungsunterlagen',
      html: registerEmployeeOnboarding({
        vorname: this.vorname ?? '',
        onboardingUrl: await Mitarbeiter.getOnboardingMagicLink(this._id),
      }),
    });
  }

  protected async getComposedOnboardingConfirmationMail(attachments?: Array<{ filename: string; content: Buffer }>) {
    return Message.compose({
      subject: 'Willkommen bei Chancenwerk – Deine nächsten Schritte',
      html: onboardingConfirmation({
        vorname: this.vorname ?? '',
        passwordUrl: (await Mitarbeiter.getPasswordChangeLink(this._id, 'chancencampus')).toString(),
        username: this.username ?? '???',
      }),
      attachments,
    });
  }

  @Mutation(() => Boolean)
  public static sendOnboardingMailForUserID(@Arg('userID', () => String) userID: string) {
    return async () => {
      const col = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      const user = constructValue(await col.findOne({ _id: userID })) as Mitarbeiter;
      if (!user) throw new Error('Invalid userID given');
      if (!user.email) throw new Error('Mitarbeiter hat keine E-Mail-Adresse hinterlegt');

      try {
        await chancenWerkMailer.sendMail(await user.getComposedOnboardingMail(), new Recipient(user.email));
        await user.saveMailEventLog();
      } catch (error) {
        await user.saveMailEventLog(error as Error);
        logger.error(
          { userID: user._id, email: user.email, error: error instanceof Error ? error.message : String(error) },
          'Couldnt send onboarding mail',
        );
        return false;
      }
      return true;
    };
  }

  @Mutation(() => Boolean)
  public static sendOnboardingConfirmationMailForUserID(@Arg('userID', () => String) userID: string) {
    return async () => {
      const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      const user = constructValue(await coll.findOne({ _id: userID })) as Mitarbeiter;
      if (!user) throw new Error('Invalid userID given');
      if (!user.email) throw new Error('Mitarbeiter hat keine E-Mail-Adresse hinterlegt');

      try {
        await chancenWerkMailer.sendMail(await user.getComposedOnboardingConfirmationMail(), new Recipient(user.email));
        await user.saveMailEventLog();
      } catch (error) {
        await user.saveMailEventLog(error as Error);
        logger.error(
          { userID: user._id, email: user.email, error: error instanceof Error ? error.message : String(error) },
          'Couldnt send onboarding confirmation mail',
        );
        return false;
      }
      return true;
    };
  }

  /**
   * Issue a short-lived signed JWT for the currently-authenticated Mitarbeiter. Consumed by the
   * onboarding Finished step to build authenticated `/api/onboarding-pdf/:kind?mitarbeiterID=…&token=…`
   * URLs — plain `<a href>` requests don't carry the BaseQL sessionID (it lives in localStorage, not
   * cookies), so we need an explicit token the handler can verify.
   */
  @Query(() => String)
  public static issueOnboardingPdfToken() {
    return async (gqlFields: void) => {
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');
      return generateLoginToken({
        userID: caller._id,
        userClass: ChancenwerkUser._class,
        forgetSession: true,
        ttu: -1,
        ttl: 1e3 * 60 * 60, // 1 hour — covers the time the user spends on the Finished step
      });
    };
  }

  @Mutation(() => Boolean)
  public static completeOnboarding(@Arg('userID', () => String) userID: string) {
    return async () => {
      // Use caller from context (ExternerMitarbeiter lacks generic modify permission)
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');
      if (userID !== caller._id) throw new Error('Nicht berechtigt');

      const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      const user = constructValue(await coll.findOne({ _id: caller._id })) as Mitarbeiter;
      if (!user) throw new Error('Invalid userID given');
      if (!user.email) throw new Error('Mitarbeiter hat keine E-Mail-Adresse hinterlegt');

      // Persist onboarding completion + vertragErhalten via raw update, bypassing permissions
      // vertragErhalten is persisted when the user confirms the contract step (via confirmOwnUnterlagen).
      // Here we only mark the onboarding itself as complete.
      await coll.updateOne({ _id: caller._id }, { $set: { 'onboarding.contractDocumentsConfirmed': true } });
      // Keep the in-memory instance consistent for mail composition below
      user.onboarding ??= new Onboarding({});
      user.onboarding.contractDocumentsConfirmed = true;

      // Return to the client as soon as the completion is persisted; PDF rendering + mail are kicked off
      // in the background so the user is not blocked on the loading spinner for the full puppeteer run.
      // All failures are logged — from the user's perspective the worst case is that the mail is late
      // or arrives without attachments (in which case the Finished step's direct download links still
      // work as a fallback).
      void (async () => {
        let attachments: Array<{ filename: string; content: Buffer }> | undefined;
        try {
          const token = await generateLoginToken({
            userID: caller._id,
            userClass: ChancenwerkUser._class,
            forgetSession: true,
            ttu: -1,
            ttl: 1e3 * 60 * 10, // 10 minutes — plenty for the 5 parallel PDF renders
          });
          attachments = await createAllOnboardingPdfAttachments({ mitarbeiterID: caller._id, token });
        } catch (error) {
          logger.error(
            { userID: user._id, error: error instanceof Error ? error.message : String(error) },
            'Could not generate onboarding PDF attachments; sending confirmation mail without them',
          );
        }

        // Send confirmation email (Email 2) with password-set link + the 5 PDF attachments
        try {
          await chancenWerkMailer.sendMail(
            await user.getComposedOnboardingConfirmationMail(attachments),
            new Recipient(user.email),
          );
          await user.saveMailEventLog();
        } catch (error) {
          await user.saveMailEventLog(error as Error);
          logger.error(
            { userID: user._id, email: user.email, error: error instanceof Error ? error.message : String(error) },
            'Couldnt send onboarding confirmation mail',
          );
        }
      })();

      return true;
    };
  }

  /**
   * Allows the currently authenticated user to update their own Stammdaten.
   * Used during onboarding where the user does not have generic 'modify' permission.
   */
  @Mutation(() => Boolean)
  public static updateOwnStammdaten(@Arg('data', () => Mitarbeiter) data: Mitarbeiter) {
    return async () => {
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');

      const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      await coll.updateOne(
        { _id: caller._id },
        {
          $set: {
            vorname: data.vorname ?? '',
            nachname: data.nachname ?? '',
            geburtstag: data.geburtstag?.toJSDate?.() ?? null,
            'adresse.strasse': data.adresse?.strasse ?? '',
            'adresse.zipCity.zip': data.adresse?.zipCity?.zip ?? '',
            'adresse.zipCity.city': data.adresse?.zipCity?.city ?? '',
            handynummer: data.handynummer ?? '',
            email: data.email ?? '',
            hochschule: data.hochschule ?? '',
            studiengang: data.studiengang ?? '',
            staatsangehoerigkeit: data.staatsangehoerigkeit ?? '',
          },
        },
      );
      return true;
    };
  }

  /**
   * Allows the currently authenticated user to confirm their own Unterlagen
   * (Datenschutz, Selbstverpflichtung, etc.) during onboarding.
   * Bypasses generic 'modify' permission that ExternerMitarbeiter lacks.
   */
  @Mutation(() => Boolean)
  public static confirmOwnUnterlagen(
    @Arg('field', () => String) field: string,
    @Arg('confirmed', () => Boolean) confirmed: boolean,
  ) {
    return async () => {
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');

      const allowed = new Set([
        'vertragErhalten',
        'datenschutzerklaerungErhalten',
        'selbstverplichtungserklaerungErhalten',
        'einwilligungserklaerungMasernimpfungNachweisErhalten',
      ]);
      if (!allowed.has(field)) throw new Error(`Feld "${field}" darf nicht selbst bestätigt werden.`);

      const value = confirmed ? new Date() : null;
      const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      await coll.updateOne({ _id: caller._id }, { $set: { [`unterlagen.${field}`]: value } });
      return true;
    };
  }

  /**
   * Allows the currently authenticated user to persist their Übungsleiterfreibetrag declaration
   * (unterlagen.freibetragInAnspruchGenommen + unterlagen.freibetragBetrag) while filling out
   * the onboarding contract page, so partial input survives reloads / returning later.
   * Bypasses generic 'modify' permission that ExternerMitarbeiter lacks.
   */
  @Mutation(() => Boolean)
  public static updateOwnFreibetrag(
    @Arg('inAnspruchGenommen', () => Boolean) inAnspruchGenommen: Maybe<boolean>,
    @Arg('betrag', () => Int) betrag: Maybe<number>,
  ) {
    return async () => {
      const caller = await getUserFromContext<Mitarbeiter>({ _id: 1 });
      if (!caller) throw new Error('Nicht angemeldet');

      const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
      await coll.updateOne(
        { _id: caller._id },
        {
          $set: {
            'unterlagen.freibetragInAnspruchGenommen': inAnspruchGenommen ?? null,
            'unterlagen.freibetragBetrag': betrag ?? null,
          },
        },
      );
      return true;
    };
  }

  /**
   * **Attention** this will throw if contract is not empty, you will need to catch it
   */
  removeContract(contractIdx: number) {
    const vertrag = this.vertraege[contractIdx];
    if (!vertrag) throw new Error(`Es wurde kein Vertrag mit dem Index ${contractIdx} gefunden.`);
    if (!vertrag.taetigkeitenIDs?.length) this.vertraege.splice(contractIdx, 1);
    else throw new Error('Der Vertrag muss leer sein um diesen zu löschen.');
  }

  public static countVertragEndingDate(
    einsatzstelle: string,
    mitarbeiter: Mitarbeiter[],
    predicateID?: string,
    time?: DateTime,
  ) {
    return mitarbeiter.reduce((acc, schuKo) => {
      const t = schuKo.findActiveTaetigkeitByEinsatzstelle(einsatzstelle, predicateID, time);
      const vertagEnd = t?.endDate;
      return acc + (vertagEnd ? 1 : 0);
    }, 0);
  }

  findActiveTaetigkeitByEinsatzstelle(einsatzstelle: string, predicateID?: string, time?: DateTime) {
    const possible = this.vertraege
      .flatMap(v => v.taetigkeiten)
      .filter(t => t?.objectID === einsatzstelle && (!predicateID || t.predicateID === predicateID));
    if (!possible) return null;

    const now = (time ?? DateTime.now()).endOf('day');
    const active = possible.filter(t => {
      if (t?.startDate && t?.startDate > now) return false;
      if (t?.endDate && t?.endDate < now) return false;
      return true;
    });

    if (!active.length) return null;
    return active[0];
  }

  private static async _leKoAggregation(
    kpfID: string | string[],
    onlyReturnCount: boolean,
    skip: number,
    limit: number,
    match?: any,
  ) {
    // * By searching through the semantic relationships we can find all employees that are currently active in a KPF
    // * way faster than searching through all employees and checking their roles and filtering afterwards.
    const collection = await getRawCollection<SemanticRelationship>(SemanticRelationship._collectionName);
    const agg = await collection
      .aggregate([
        // * 1. find all active LEKOs
        {
          $match: {
            $and: [
              { predicateID: Role.LEKO },
              typeof kpfID === 'string' ? { objectID: kpfID } : { objectID: { $in: kpfID } },
              Taetigkeit.isActiveMongoSelector(),
            ],
          },
        },

        // * 2. fetch the user
        {
          $lookup: { from: Mitarbeiter._collectionName, localField: 'subjectID', foreignField: '_id', as: 'employee' },
        },

        // * 3. user array to user object
        { $unwind: '$employee' },

        // * 4. pull the users out so that we have a array of users
        { $replaceRoot: { newRoot: '$employee' } },

        // * 5. match the publish filter
        ...(match ? [{ $match: match }] : []),

        // * 6. count or sort, skip, limit
        ...(onlyReturnCount ? [{ $count: 'count' }] : [{ $sort: { nachname: 1 } }, { $skip: skip }, { $limit: limit }]),
      ])
      .toArray();

    if (onlyReturnCount) {
      return ((agg[0] ?? { count: 0 }) as any).count as number;
    }

    return agg as any as EntityData<Mitarbeiter>[];
  }

  static _leKoAggregationSub(
    kpfID: string,
    onlyReturnCount: boolean,
    skip: number,
    limit: number,
    enablePublishFilter: boolean = true,
  ) {
    const handle = getCollectionHandle<Mitarbeiter>(this._collectionName);
    return from(publishFilter(this._class)).pipe(
      switchMap(publishFilter => {
        const selector = {};
        return handle
          .watchQuery(
            publishFilter && enablePublishFilter ? { $and: [publishFilter, selector] } : selector,
            onlyReturnCount ? {} : { skip, limit },
          )
          .pipe(mapTo({ ...selector, ...(enablePublishFilter ? publishFilter : {}) }));
      }),
      switchMap(async docs => {
        if (!onlyReturnCount) {
          const users = (await this._leKoAggregation(kpfID, onlyReturnCount, skip, limit, docs)) as Array<
            EntityData<Mitarbeiter>
          >;
          return users.map(u => constructValue(u) as Mitarbeiter);
        }

        const usersCount = (await this._leKoAggregation(kpfID, onlyReturnCount, skip, limit, docs)) as number;
        return usersCount;
      }),
    );
  }

  @Query(() => [Mitarbeiter])
  public static getPublicLeKoByKPF(@Arg('kpfID', () => String) kpfID: string) {
    return async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
      const user = await getUserFromContext<ChancenwerkUser>({ roles: ChancenRole.allFields });
      if (!user) {
        throw new Error('unauthorized');
      }

      if (
        !user.hasPermission(ChancenPermission.board.VIEW_ATTENDANCE) &&
        !user.hasPermission(ChancenPermission.board.EXPORT_ATTENDANCE)
      ) {
        throw new Error('unauthorized');
      }

      const users = (await this._leKoAggregation(kpfID, false, 0, 1000, false)) as EntityData<Mitarbeiter>[];
      return users.map(u => constructValue(u) as Mitarbeiter);
    };
  }

  @Subscription(() => Int)
  public static subscribeNumberOfLeKoByKPF(
    @Arg('skip', () => Int) skip: number,
    @Arg('limit', () => Int) limit: number,
    @Arg('kpfID', () => String) kpfID: string,
  ) {
    return (gqlFields: void) => this._leKoAggregationSub(kpfID, true, skip, limit) as Observable<number>;
  }

  @Mutation(() => Mitarbeiter)
  static getMitarbeiterFromSoftgardenKey(@Arg('key', () => String) key: string) {
    return async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
      return getMitarbeiterFromSoftgardenKeyFromCache(key);
    };
  }
}

if (A.isServer) {
  void (async () => {
    const coll = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
    await coll.createIndex({ employeeID: 1 });
  })();
}

export const getMitarbeiterWithOneTimeToken = registerQuery({
  operationName: 'getMitarbeiterWithOneTimeToken',
  resolve: (mitarbeiterID: string, token: string) => async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
    await verifyToken(token);
    const rawCol = await getRawCollection<Mitarbeiter>(Mitarbeiter._collectionName);
    const foundMA = await rawCol.findOne({ _id: mitarbeiterID });
    return foundMA;
  },
  type: () => Mitarbeiter,
  params: [
    { name: 'mitarbeiterID', type: () => ID },
    { name: 'token', type: () => String },
  ],
});
